import { useState } from 'react';
import { ActionsDialogue, Bouton, Carte, Champ, Dialogue, Message } from '@journal/ui';

/**
 * Réglages → Suppressions : exporter ses données, et fermer l'espace.
 *
 * ── L'export est ouvert à TOUS ──────────────────────────────────────────────
 * Un seul fichier CSV, qui réunit tous les modules. La direction — propriétaire
 * ou administrateur — emporte toute l'école en un clic ; un enseignant emporte
 * ce qui est à lui : ses classes, son agenda, ses 108 heures, sa liste « À
 * faire ». Il ne peut pas fermer l'espace, mais il ne peut pas être privé de
 * son propre travail.
 *
 * ── La suppression est réservée au PROPRIÉTAIRE ─────────────────────────────
 * Le serveur l'exige. Un enseignant d'école voit ce qui la concerne — à qui
 * s'adresser — plutôt qu'un bouton qui refuserait.
 *
 * ── Une fenêtre de confirmation, avec le MOT DE PASSE ───────────────────────
 * Le bouton ouvre une fenêtre qui redit ce qui va se passer et demande le mot
 * de passe du propriétaire — de l'enseignant pour un espace individuel, de la
 * direction pour une école. Le serveur le vérifie : c'est la preuve que c'est
 * bien le titulaire du compte qui ferme l'espace.
 *
 * ── Récupérable 30 jours ────────────────────────────────────────────────────
 * La suppression ferme l'espace tout de suite, résilie l'abonnement au terme de
 * la période payée, et s'annule en se reconnectant pendant 30 jours.
 */

export function ReglagesSuppressions({
  surSuppression, surExport, enCours, estProprietaire, exportEcole,
}: {
  /**
   * Reçoit le mot de passe saisi. Rend le message d'erreur à afficher dans la
   * fenêtre — mot de passe incorrect —, ou `null` si la suppression est faite.
   */
  surSuppression: (motDePasse: string) => Promise<string | null>;
  /** Seul le propriétaire ferme l'espace. */
  estProprietaire: boolean;
  /** La direction exporte toute l'école ; un enseignant, ses propres données. */
  exportEcole: boolean;
  surExport: () => void;
  enCours: boolean;
}) {
  const [ouverte, setOuverte] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  function fermer() {
    setOuverte(false);
    setMotDePasse('');
    setErreur(null);
  }

  async function confirmer() {
    setEnvoi(true);
    setErreur(null);
    const echec = await surSuppression(motDePasse);
    setEnvoi(false);
    if (echec) setErreur(echec);
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <Carte titre="Exporter les données">
        <div className="flex flex-col gap-3">
          <div>
            <Bouton variante="secondary" onClick={surExport} chargement={enCours}>
              {exportEcole ? 'Exporter les données de l’école' : 'Exporter mes données'}
            </Bouton>
          </div>
        </div>
      </Carte>

      {!estProprietaire ? (
        /*
         * Un enseignant d'école voit ce qui le concerne — à qui s'adresser —
         * plutôt qu'un bouton qui refuserait.
         */
        <Carte titre="Supprimer l’espace">
          <Message ton="info" titre="Réservé au propriétaire de l’espace">
            Seul le propriétaire peut fermer l’espace de l’école. Dans une école,
            c’est la direction, qui peut aussi retirer un enseignant depuis
            l’écran <strong>Équipe</strong>, en le choisissant dans la liste.
          </Message>
        </Carte>
      ) : (
        <Carte titre="Supprimer l’espace" className="border-[var(--color-danger)]">
          <div className="flex flex-col gap-4">
            <p className="text-[13px] leading-relaxed text-[var(--color-fg-muted)]">
              {'Cela efface '}
              <strong>toutes</strong>
              {' les données de l’école : cahier journal, progressions, programmation, '}
              {'emploi du temps, évaluations, élèves, agenda, périodes et années '}
              {'scolaires. L’abonnement est résilié au terme de la période déjà payée.'}
            </p>

            <Message ton="attention" titre="Récupérable pendant 30 jours, puis définitif">
              L’espace est fermé tout de suite. Pendant 30 jours, vous pouvez le
              réactiver en vous reconnectant ; passé ce délai, tout est effacé sans
              retour. Exportez vos données avant si vous souhaitez en garder une
              copie.
            </Message>

            <div>
              <Bouton variante="danger" onClick={() => setOuverte(true)}>
                Supprimer l’espace…
              </Bouton>
            </div>
          </div>
        </Carte>
      )}

      <Dialogue
        titre="Supprimer l’espace ?"
        ouvert={ouverte}
        surFermeture={fermer}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => { e.preventDefault(); if (motDePasse && !envoi) void confirmer(); }}
        >
          <p className="text-[14px] leading-relaxed">
            L’espace sera fermé tout de suite et l’abonnement résilié au terme de la
            période déjà payée. Pendant 30 jours, vous pourrez le réactiver en vous
            reconnectant ; ensuite, tout sera effacé sans retour.
          </p>
          {erreur && <Message ton="erreur">{erreur}</Message>}
          <Champ
            libelle="Votre mot de passe"
            type="password"
            autoComplete="current-password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoFocus
          />
          <ActionsDialogue>
            <Bouton variante="ghost" onClick={fermer}>Annuler</Bouton>
            <Bouton type="submit" variante="danger" chargement={envoi} disabled={motDePasse === ''}>
              Supprimer l’espace
            </Bouton>
          </ActionsDialogue>
        </form>
      </Dialogue>
    </div>
  );
}
